import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import PageHero from "@/components/shared/PageHero";
import api from "@/lib/api";

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [category, setCategory] = useState("all");

  useEffect(() => { api.get("/gallery").then((r) => setItems(r.data)).catch(() => {}); }, []);

  const cats = ["all", ...Array.from(new Set(items.map((i) => i.category).filter(Boolean)))];
  const filtered = category === "all" ? items : items.filter((i) => i.category === category);

  return (
    <div>
      <PageHero
        label="Business Gallery"
        title="Inside the Hermanos business."
        subtitle="Warehouse, showroom, packaging, dispatch — a behind-the-scenes look at how we deliver garments across Bharat."
        breadcrumbs={[{ label: "Gallery" }]}
      />

      <section className="py-8 border-b border-border/60">
        <div className="mx-auto max-w-7xl px-4 md:px-8 flex flex-wrap gap-2">
          {cats.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={`px-4 h-9 rounded-sm text-xs tracking-widest uppercase border transition-colors ${
                category === c
                  ? "border-gold bg-gold text-navy-900 font-semibold"
                  : "border-border/60 text-foreground/70 hover:border-gold/60 hover:text-gold"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      <section className="py-14">
        <div className="mx-auto max-w-7xl px-4 md:px-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {filtered.map((g, i) => (
            <motion.button
              key={g.id}
              initial={{ opacity: 0, scale: 0.98 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.02 }}
              onClick={() => setActive(g)}
              className={`overflow-hidden rounded-md border border-border/60 hover:border-gold/60 transition-colors ${i % 5 === 0 ? "row-span-2 aspect-[1/2]" : "aspect-square"}`}
              data-testid={`gallery-item-${i}`}
            >
              <img src={g.image} alt={g.title} loading="lazy" decoding="async" className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000" />
            </motion.button>
          ))}
        </div>
      </section>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 backdrop-blur flex items-center justify-center p-4"
            onClick={() => setActive(null)}
          >
            <button className="absolute top-6 right-6 h-11 w-11 rounded-sm border border-white/30 text-white flex items-center justify-center hover:bg-white/10" onClick={() => setActive(null)}>
              <X size={18} />
            </button>
            <motion.img
              initial={{ scale: 0.95 }} animate={{ scale: 1 }}
              src={active.image}
              alt={active.title}
              className="max-h-[85vh] max-w-[90vw] object-contain rounded-md"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
